import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { useListKadryGatesQuery, useSetKadryGateMutation } from '@shared/api';
import { kadryDateTime, useKadryAccess, type KadryGateState } from '@entities/kadry';
import {
  KadryBannerGap,
  KadryEmpty,
  KadryErrorBanner,
  KadryMono,
  KadryMuted,
  KadryPerson,
  KadryReasonDialog,
} from '../ui';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Row = styled(Card)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(5)}`};
  align-items: start;
  padding: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const Line = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;

  & + & {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const Intro = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

/**
 * Фичегейты модуля (ADR-0019 §3): что закрывает функция, условие включения,
 * кто и когда менял. Переключение — с обязательным основанием (в журнал) и
 * If-Match по версии гейта. Мастер-флаг `module` задаётся окружением и здесь
 * только отображается.
 */
export function FeatureGates() {
  const { data, isLoading, error, refetch } = useListKadryGatesQuery();
  // В режиме «только чтение» функции видны, но не переключаются.
  const editable = useKadryAccess().canWrite('admin_settings');
  const [target, setTarget] = useState<KadryGateState | null>(null);
  const [setGate, setState] = useSetKadryGateMutation();

  async function onConfirm(comment: string) {
    if (!target) return;
    try {
      await setGate({
        gate: target.gate,
        version: target.version,
        body: { enabled: !target.enabled, comment },
      }).unwrap();
      setTarget(null);
    } catch {
      // Ошибка показывается в диалоге.
    }
  }

  if (isLoading) {
    return (
      <Card>
        <SkeletonLine $width="40%" />
        <SkeletonLine />
        <SkeletonLine $width="60%" />
      </Card>
    );
  }
  if (error) {
    return <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить функции" />;
  }
  const gates = data ?? [];
  if (gates.length === 0) {
    return (
      <Card>
        <KadryEmpty>Список функций пуст.</KadryEmpty>
      </Card>
    );
  }

  return (
    <>
      <Intro>
        <KadryMuted>
          Все функции по умолчанию выключены. Функция открывает возможность, но не отменяет прочих
          условий: утверждённого профиля обработки, активного контекста, бюджета и квот. Проверка
          выполняется на сервере.
        </KadryMuted>
      </Intro>
      {setState.error && !target ? (
        <KadryBannerGap>
          <KadryErrorBanner error={setState.error} onRefresh={refetch} onDismiss={() => setState.reset()} />
        </KadryBannerGap>
      ) : null}
      <List>
        {gates.map((g) => {
          const master = g.gate === 'module';
          return (
            <Row key={g.gate}>
              <div>
                <Head>
                  <Title>{g.title}</Title>
                  <KadryMono>{g.gate}</KadryMono>
                </Head>
                <Line>
                  <strong>Условие включения:</strong> {g.condition}
                </Line>
                {master ? (
                  <Line>
                    Задаётся переменной окружения <KadryMono>OSNOVA_KADRY_ENABLED</KadryMono> на
                    сервере, из интерфейса не переключается.
                  </Line>
                ) : g.changed_at ? (
                  <Line>
                    <strong>Изменил:</strong> <KadryPerson id={g.changed_by} /> ·{' '}
                    {kadryDateTime(g.changed_at)}
                    {g.comment ? ` · «${g.comment}»` : ''}
                  </Line>
                ) : (
                  <Line>Не менялась с установки.</Line>
                )}
              </div>
              <Side>
                {g.enabled ? <Badge $tone="success">Включена</Badge> : <Badge>Выключена</Badge>}
                {!master && editable && (
                  <Button
                    type="button"
                    $size="sm"
                    $variant={g.enabled ? 'secondary' : 'primary'}
                    disabled={setState.isLoading}
                    onClick={() => {
                      setState.reset();
                      setTarget(g);
                    }}
                  >
                    {g.enabled ? 'Выключить' : 'Включить'}
                  </Button>
                )}
              </Side>
            </Row>
          );
        })}
      </List>

      <KadryReasonDialog
        open={target != null}
        title={target ? `${target.enabled ? 'Выключить' : 'Включить'}: ${target.title}` : ''}
        description={
          target?.enabled
            ? 'Функция перестанет работать сразу после сохранения — операции, которые она открывает, будут отклоняться сервером.'
            : undefined
        }
        warning={
          target && !target.enabled ? (
            <>
              Включайте только после выполнения условия: {target.condition}.
            </>
          ) : undefined
        }
        confirmLabel={target?.enabled ? 'Выключить' : 'Включить'}
        confirmVariant={target?.enabled ? 'danger' : 'primary'}
        reasonLabel="Основание"
        reasonPlaceholder="Ссылка на решение, договор, служебную записку"
        reasonHint="Попадёт в журнал аудита и будет видно в списке функций."
        reasonRequired
        reasonMaxLength={500}
        busy={setState.isLoading}
        errorSlot={
          setState.error ? (
            <KadryErrorBanner
              error={setState.error}
              onRefresh={() => {
                setState.reset();
                setTarget(null);
                refetch();
              }}
            />
          ) : undefined
        }
        onConfirm={onConfirm}
        onCancel={() => {
          setTarget(null);
          setState.reset();
        }}
      />
    </>
  );
}
